/*
 * Copyright (c) 2023 SARL SIGMA POLYNESIA
 */

import { DateTime } from 'luxon'
import {
  INVOICE_CREDIT,
  INVOICE_ESTIMATION,
  INVOICE_LESSOR_FIRST_INVOICE,
  INVOICE_MANAGEMENT,
  INVOICE_OWNER,
  INVOICE_PROFORMA,
  INVOICE_RENT,
  INVOICE_SALE,
  INVOICE_TENANT,
  INVOICE_TENANT_FIRST_INVOICE
} from '../../enums/InvoiceTypes.mjs'

/**
 * Returns the reference of a credit invoice.
 * @param {string} date
 * @param {string} estateRef
 * @return {string}
 */
export function generateCreditInvoiceReference (date, estateRef) {
  const dt = DateTime.fromISO(date)
  return ['CR', dt.toFormat('yyyyMMdd'), estateRef].join('')
}

/**
 * Returns the reference of an estimation invoice.
 * @param {string} date
 * @param {string} estateRef
 * @return {string}
 */
export function generateEstimationInvoiceReference (date, estateRef) {
  const dt = DateTime.fromISO(date)
  return ['ES', dt.toFormat('yyyyMMdd'), estateRef].join('')
}

/**
 * Returns the reference of a lessor fees invoice.
 * @param {string} date
 * @param {string} estateRef
 * @return {string}
 */
export function generateLessorFirstInvoiceReference (date, estateRef) {
  const dt = DateTime.fromISO(date)
  return ['LF', dt.toFormat('yyyyMM'), estateRef].join('')
}

/**
 * Returns the reference of a management invoice.
 * @param {string} date
 * @param {string} estateRef
 * @return {string}
 */
export function generateManagementInvoiceReference (date, estateRef) {
  const dt = DateTime.fromISO(date)
  return ['M', dt.toFormat('yyyyMM'), estateRef].join('')
}

/**
 * Returns the reference of an owner invoice.
 * @param {string} date
 * @param {string} estateRef
 * @return {string}
 */
export function generateOwnerInvoiceReference (date, estateRef) {
  const dt = DateTime.fromISO(date)
  return ['OW', dt.toFormat('yyyyMMdd'), estateRef].join('')
}

/**
 * Returns the reference of a pro forma invoice.
 * @param {string} date
 * @param {string} estateRef
 * @return {string}
 */
export function generateProformaInvoiceReference (date, estateRef) {
  const dt = DateTime.fromISO(date)
  return ['PR', dt.toFormat('yyyyMMdd'), estateRef].join('')
}

/**
 * Returns the reference of a rent invoice.
 * @param {string} date
 * @param {string} estateRef
 * @return {string}
 */
export function generateRentInvoiceReference (date, estateRef) {
  const dt = DateTime.fromISO(date)
  return ['R', dt.toFormat('yyyyMM'), estateRef].join('')
}

/**
 * Returns the reference of a sale invoice.
 * @param {string} date
 * @param {string} estateRef
 * @return {string}
 */
export function generateSaleInvoiceReference (date, estateRef) {
  const dt = DateTime.fromISO(date)
  return ['SA', dt.toFormat('yyyyMMdd'), estateRef].join('')
}

/**
 * Returns the reference of a tenant fees invoice.
 * @param {string} date
 * @param {string} estateRef
 * @return {string}
 */
export function generateTenantFirstInvoiceReference (date, estateRef) {
  const dt = DateTime.fromISO(date)
  return ['TF', dt.toFormat('yyyyMM'), estateRef].join('')
}

/**
 * Returns the reference of a tenant invoice.
 * @param {string} date
 * @param {string} estateRef
 * @return {string}
 */
export function generateTenantInvoiceReference (date, estateRef) {
  const dt = DateTime.fromISO(date)
  return ['TE', dt.toFormat('yyyyMMdd'), estateRef].join('')
}

/**
 * Returns the invoice reference from attributes.
 * @param invoice
 * @param {string} estateRef
 * @return {string}
 */
export function generateInvoiceReference (invoice, estateRef) {
  const dt = DateTime.fromISO(invoice.dueAt)
  const dateIso = dt.toISO()
  let reference

  if (invoice.type === INVOICE_ESTIMATION) {
    reference = generateEstimationInvoiceReference(dateIso, estateRef)
  } else if (invoice.type === INVOICE_CREDIT) {
    reference = generateCreditInvoiceReference(dateIso, estateRef)
  } else if (invoice.type === INVOICE_PROFORMA) {
    reference = generateProformaInvoiceReference(dateIso, estateRef)
  } else if (invoice.type === INVOICE_SALE) {
    reference = generateSaleInvoiceReference(dateIso, estateRef)
  } else if (invoice.type === INVOICE_RENT) {
    reference = generateRentInvoiceReference(dateIso, estateRef)
  } else if (invoice.type === INVOICE_MANAGEMENT) {
    reference = generateManagementInvoiceReference(dateIso, estateRef)
  } else if (invoice.type === INVOICE_OWNER) {
    reference = generateOwnerInvoiceReference(dateIso, estateRef)
  } else if (invoice.type === INVOICE_LESSOR_FIRST_INVOICE) {
    reference = generateLessorFirstInvoiceReference(dateIso, estateRef)
  } else if (invoice.type === INVOICE_TENANT) {
    reference = generateTenantInvoiceReference(dateIso, estateRef)
  } else if (invoice.type === INVOICE_TENANT_FIRST_INVOICE) {
    reference = generateTenantFirstInvoiceReference(dateIso, estateRef)
  } else {
    throw new Error(`Unsupported invoice type "${invoice.type}".`)
  }
  return reference
}
